<template>
    <app-framework>
      <header class="container">
        <bread-crumbs :list="[
          { name: $t('home'), link: '/' },
          { name: $t('staff_member_listing') }
        ]" />
        <h1>{{ $t('staff_member_listing') }}</h1>
      </header>
      <article class="container framework-slot">
        <div class="form-row toolbar">
            <div class="col col-6 col-md-auto">
                <router-link to="/staff-members/new" class="btn btn-primary">{{ $t("new_button") }}</router-link>
            </div>
            <div class="col  d-none d-md-block"></div>
            <div class="col col-6 col-md-auto text-right">
                <button class="btn btn-outline-secondary" :title="$t('refresh_data')" :disabled="staffMembers.loading"
                        v-on:click="refreshData">
                    <font-awesome-icon :spin="staffMembers.loading" :icon="['fal', 'sync-alt']"/>
                </button>
            </div>
            <div class="col col-12 col-sm-6 col-md-auto">
                <input-group>
                    <select class="custom-select" :aria-label="$t('role')" v-model="form.roleId"
                            v-on:change="filter.roleId = form.roleId">
                        <option :value="null">
                            <template v-if="roles.loading">{{ $t("loading") }}</template>
                            <template v-else-if="roles.error">{{ $t("error_load_roles") }}</template>
                            <template v-else>{{ $t("all_roles") }}</template>
                        </option>
                        <option v-for="role in roles.nodes" :key="role.id" :value="role.id">{{role.name}}</option>
                    </select>
                </input-group>
            </div>
            <div class="col col-12 col-sm-6 col-md-auto">
                <input-group>
                    <input type="text" class="form-control" :placeholder="$t('search_placeholder')"
                           :aria-label="$t('search')" v-model="form.textSearch" v-on:keydown="textSearchKeyDown">
                    <template v-slot:append-slot>
                        <button class="btn btn-outline-secondary"
                                v-on:click="filter.textSearch = (form.textSearch || null)">
                            <template v-if="(form.textSearch || null) !== filter.textSearch">{{ $t('search_button') }}
                            </template>
                            <font-awesome-icon v-else :icon="['fal', 'search']"/>
                        </button>
                    </template>
                </input-group>
            </div>
        </div>
        <div v-if="staffMembers.loading || roles.loading">
            <loading-state/>
        </div>
        <div v-else-if="staffMembers.error || roles.error">
            <request-error-state :try-again-handler="refreshData" :message="staffMembers.error || roles.error"/>
        </div>
        <div v-else-if="!staffMembers.data.nodes || !staffMembers.data.nodes.length">
            <empty-state/>
        </div>
        <infinite-scroll v-else :handler="queryMoreStaffMembers"
                         :should-handle="staffMembers.data.pageInfo.hasNextPage">
            <div class="row">
                <template v-for="staffMember in staffMembers.data.nodes">
                    <div :key="staffMember.id" class="col-12 col-md-6 col-lg-4">
                        <div class="result-entity">
                            <h3>{{staffMember.name}}</h3>
                            <table class="table table-bordered">
                                <tr>
                                    <th>{{ $t("role") }}:</th>
                                    <td colspan="2">{{staffMember.role.name}}</td>
                                </tr>
                                <tr>
                                    <th>{{ $t("status") }}:</th>
                                    <td class="no-border-right">
                                        {{staffMember.isBanned ? $t("status_banned") : $t("status_not_banned")}}
                                    </td>
                                    <td class="with-action shrink">
                                        <button v-if="staffMember.isBanned" class="btn btn-sm btn-outline-success"
                                                @click="revokeBanStaffMember(staffMember)"
                                                :disabled="staffMembers.banningMap[staffMember.id]">
                                            {{staffMembers.banningMap[staffMember.id] ? $t("revoking_ban") : $t("revoke_ban_button")}}
                                        </button>
                                        <button v-else class="btn btn-sm btn-outline-danger"
                                                @click="banStaffMember(staffMember)"
                                                :disabled="staffMembers.banningMap[staffMember.id]">
                                            {{staffMembers.banningMap[staffMember.id] ? $t("banning") : $t("ban_button")}}
                                        </button>
                                    </td>
                                </tr>
                            </table>
                            <div class="messages alert-danger" v-if="staffMembers.actionErrorMap[staffMember.id]">
                                {{staffMembers.actionErrorMap[staffMember.id]}}
                            </div>
                            <div class="actions">
                                <router-link :to="'/staff-members/' + staffMember.id"
                                             class="btn btn-sm btn-outline-secondary">
                                    {{ $t('edit_button') }}
                                </router-link>
                                <button class="btn btn-sm btn-outline-danger" @click="deleteStaffMember(staffMember)"
                                        :disabled="staffMembers.deletingMap[staffMember.id]">
                                    {{staffMembers.deletingMap[staffMember.id] ? $t("deleting") : $t("delete_button")}}
                                </button>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </infinite-scroll>
      </article>
    </app-framework>
</template>

<script>
    import AppFramework from "@/components/layout/AppFramework";
    import InputGroup from "@/components/common/InputGroup";
    import InfiniteScroll from "@/components/common/InfiniteScroll";
    import LoadingState from "@/components/states/LoadingState";
    import EmptyState from "@/components/states/EmptyState";
    import RequestErrorState from "@/components/states/RequestErrorState";
    import {ROLES} from "@/gql/Roles";
    import {
        BAN_STAFF_MEMBER,
        DELETE_STAFF_MEMBER,
        STAFF_MEMBERS, REVOKE_BAN_STAFF_MEMBER
    } from "@/gql/StaffMembers";
    import BreadCrumbs from "@/components/layout/BreadCrumbs";

    export default {
        name: 'staffMemberListing',
        i18nOptions: {namespaces: ['staff_member_listing', 'common', 'breadcrumbs']},
        components: {BreadCrumbs, RequestErrorState, EmptyState, LoadingState, InfiniteScroll, InputGroup, AppFramework},
        metaInfo() {
          return {
            title: this.$t('staff_member_listing')
          }
        },
        data: () => ({
            filter: {
                textSearch: null,
                roleId: null
            },
            form: {
                textSearch: "",
                active: true,
                roleId: null
            },
            staffMembers: {
                data: null,
                loading: true,
                error: null,
                banningMap: {},
                deletingMap: {},
                actionErrorMap: {}
            },
            roles: {
                nodes: null,
                loading: true,
                error: null
            }
        }),
        watch: {
            filter: {
                deep: true,
                handler() {
                    this.queryStaffMembers();
                }
            }
        },
        mounted() {
            this.queryStaffMembers();
            this.queryRoles();
        },
        methods: {
            async queryRoles(useCache = true) {
                this.roles.loading = true;
                try {
                    const data = await this.$apolloClient.queryAll("roles", {
                        query: ROLES,
                        variables: {
                            first: 100
                        },
                        fetchPolicy: useCache ? "cache-first" : "network-only"
                    });
                    this.roles.nodes = data.nodes;
                    this.roles.error = null;
                } catch (e) {
                    if (!e.graphQLErrors || !e.graphQLErrors.some(({extensions, message}) => {
                        if (extensions && extensions.code === "SHOW_ERROR_TO_USER") {
                            this.roles.error = message;
                            return true;
                        }
                        return false;
                    })) {
                        this.roles.error = "Error to load roles";
                    }
                }
                this.roles.loading = false;
            },
            async queryStaffMembers(useCache = true) {
                this.staffMembers.deletingMap = {};
                this.staffMembers.actionErrorMap = {};
                this.staffMembers.loading = true;
                try {
                    const response = await this.$apolloClient.query({
                        query: STAFF_MEMBERS,
                        variables: {
                            textSearch: this.filter.textSearch,
                            roleId: this.filter.roleId,
                            first: 21,
                        },
                        fetchPolicy: useCache ? "cache-first" : "network-only"
                    });
                    this.staffMembers.data = {...response.data.staffMembers};
                    this.staffMembers.error = null;
                } catch (e) {
                    if (!e.graphQLErrors || !e.graphQLErrors.some(({extensions, message}) => {
                        if (extensions && extensions.code === "SHOW_ERROR_TO_USER") {
                            this.staffMembers.error = message;
                            return true;
                        }
                        return false;
                    })) {
                        this.staffMembers.error = "Error to load staff members";
                    }
                }
                this.staffMembers.loading = false;
            },
            async queryMoreStaffMembers() {
                const response = await this.$apolloClient.query({
                    query: STAFF_MEMBERS,
                    variables: {
                        textSearch: this.filter.textSearch,
                        roleId: this.filter.roleId,
                        first: 21,
                        after: this.staffMembers.data.pageInfo.endCursor
                    },
                    fetchPolicy: "no-cache"
                });
                this.staffMembers.data.nodes = [...this.staffMembers.data.nodes, ...response.data.staffMembers.nodes];
                this.staffMembers.data.pageInfo = response.data.staffMembers.pageInfo;
            },
            async refreshData() {
                await Promise.all([this.queryStaffMembers(false), this.queryRoles(false)]);
            },
            textSearchKeyDown(event) {
                if (event && (event.key === "enter" || event.keyCode === 13)) {
                    this.filter.textSearch = (this.form.textSearch || null);
                }
            },
            async banStaffMember(staffMember) {
                this.$set(this.staffMembers.banningMap, staffMember.id, true);
                this.$set(this.staffMembers.actionErrorMap, staffMember.id, null);
                try {
                    await this.$apolloClient.mutate({
                        mutation: BAN_STAFF_MEMBER,
                        variables: {
                            id: staffMember.id
                        }
                    });
                    staffMember.isBanned = true;
                } catch (e) {
                    if (!e.graphQLErrors || !e.graphQLErrors.some(({extensions, message}) => {
                        if (extensions && extensions.code === "SHOW_ERROR_TO_USER") {
                            this.$set(this.staffMembers.actionErrorMap, staffMember.id, message);
                            return true;
                        }
                        return false;
                    })) {
                        this.$set(this.staffMembers.actionErrorMap, staffMember.id, "Error banning staff member");
                    }
                }
                this.$set(this.staffMembers.banningMap, staffMember.id, false);
            },
            async revokeBanStaffMember(staffMember) {
                this.$set(this.staffMembers.banningMap, staffMember.id, true);
                this.$set(this.staffMembers.actionErrorMap, staffMember.id, null);
                try {
                    await this.$apolloClient.mutate({
                        mutation: REVOKE_BAN_STAFF_MEMBER,
                        variables: {
                            id: staffMember.id
                        }
                    });
                    staffMember.isBanned = false;
                } catch (e) {
                    if (!e.graphQLErrors || !e.graphQLErrors.some(({extensions, message}) => {
                        if (extensions && extensions.code === "SHOW_ERROR_TO_USER") {
                            this.$set(this.staffMembers.actionErrorMap, staffMember.id, message);
                            return true;
                        }
                        return false;
                    })) {
                        this.$set(this.staffMembers.actionErrorMap, staffMember.id, "Error revoking staff member ban");
                    }
                }
                this.$set(this.staffMembers.banningMap, staffMember.id, false);
            },
            async deleteStaffMember(staffMember) {
                this.$set(this.staffMembers.deletingMap, staffMember.id, true);
                this.$set(this.staffMembers.actionErrorMap, staffMember.id, null);
                try {
                  await this.$apolloClient.mutate({
                    mutation: DELETE_STAFF_MEMBER,
                    variables: {
                      id: staffMember.id
                    }
                  })
                  const staffMemberIndex = this.staffMembers.data.nodes.indexOf(staffMember)
                  this.staffMembers.data.nodes.splice(staffMemberIndex, 1)
                  this.$apolloClient.cache.data.set(`StaffMember:${staffMember.id}`, undefined)
                } catch (e) {
                    if (!e.graphQLErrors || !e.graphQLErrors.some(({extensions, message}) => {
                        if (extensions && extensions.code === "SHOW_ERROR_TO_USER") {
                            this.$set(this.staffMembers.actionErrorMap, staffMember.id, message);
                            return true;
                        }
                        return false;
                    })) {
                        this.$set(this.staffMembers.actionErrorMap, staffMember.id, "Error deleting record");
                    }
                }
                this.$set(this.staffMembers.deletingMap, staffMember.id, false);
            }
        }
    }
</script>
